import tables, {PAGE_SIZES} from 'constants/tables'
import {useEffect, useState} from 'react'
import {useUrlParams} from 'services/hooks'
import {getItemId, getTableData} from 'services/utils/tableUtils'
import {onDelete} from 'services/utils/toastUtils'
import {useTranslation} from 'react-i18next'

function objectToURLParams(obj) {
    const params = new URLSearchParams()
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (obj[key]) params.append(key, obj[key])
        }
    }
    return params.toString()
}

const useTable = (
    itemName,
    useGetQuery,
    paginationType,
    useDeleteMutation,
    visibleKeys,
    allowExpand
) => {
    const tableName = `${itemName}s`

    const {t} = useTranslation()

    const {urlParams} = useUrlParams()
    const {
        search,
        [`${itemName}_page`]: page,
        [`${itemName}_pageSize`]: pageSize,
        [`${itemName}_sort`]: sort,
        ...resParams
    } = urlParams

    // If no sort in URL yet, use the table's default desc sort key so the first
    // query already has the correct params — preventing a duplicate API call when
    // useTableSort's mount effect sets the default sort after the first render.
    const defaultSortKey = tables[tableName]?.sortTableKeysDefaultDesc?.[0]
    const effectiveSort =
        sort || (defaultSortKey ? JSON.stringify({[defaultSortKey]: 'desc'}) : undefined)

    const [cachedTotal, setCachedTotal] = useState(null)

    const {data, isFetching: isLoadingTableData} = useGetQuery(
        paginationType === 'external'
            ? objectToURLParams({
                  page: page || 0,
                  pageSize: pageSize || PAGE_SIZES[2],
                  search,
                  sort: effectiveSort,
                  includeFilters: 'true',
                  ...resParams,
              })
            : {}
    )

    const [errorMessage, setErrorMessage] = useState(null)
    const [doDelete, {errorDelete}] = useDeleteMutation()

    useEffect(() => {
        setErrorMessage(errorDelete?.data?.error)
    }, [errorDelete])

    const {
        tableKeys,
        tableData,
        keyTypes,
        tableItems,
        totalItems: rawTotal,
        filters,
        keyTranslate,
        externalKeys,
    } = getTableData(tableName, visibleKeys, data, page, pageSize, search, sort, paginationType)

    useEffect(() => {
        if (rawTotal != null) setCachedTotal(rawTotal)
    }, [rawTotal])

    const totalItems = cachedTotal ?? rawTotal

    const onDeleteHandler = (itemData) => onDelete(itemData, itemName, doDelete, t)

    const [expandedId, setExpandedId] = useState(null)

    const handleExpand = () => (allowExpand ? setExpandedId : null)

    const getExpandedItem = () => tableItems.find((item) => getItemId(item) === expandedId)

    return {
        tableName,
        tableKeys,
        tableData,
        keyTypes,
        keyTranslate,
        externalKeys,
        tableItems,
        totalItems,
        errorMessage,
        expandedId,
        filters,
        doDelete: onDeleteHandler,
        handleExpand,
        getExpandedItem,
        isLoadingTableData,
    }
}

export default useTable
